import { useState } from "react";
import { Check, Copy } from "lucide-react";

import { cn } from "~/lib/cn";

interface CopyButtonProps {
    value: string;
    label?: string;
    className?: string;
}

/**
 * Tiny "copy to clipboard" affordance. Flips to a checkmark for 1.4s after
 * a successful copy. Falls back silently if the Clipboard API is unavailable
 * (e.g., insecure origins).
 */
export function CopyButton({ value, label = "Copy", className }: CopyButtonProps): React.ReactElement {
    const [copied, setCopied] = useState(false);

    async function copy(): Promise<void> {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
        } catch {
            // Clipboard not allowed — silent.
        }
    }

    return (
        <button
            type="button"
            onClick={copy}
            className={cn("btn btn--ghost btn--sm press-crush", className)}
            aria-label={copied ? "Copied" : label}
            title={copied ? "Copied" : label}
        >
            {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
            <span>{copied ? "Copied" : label}</span>
        </button>
    );
}
